import { computed, inject, Injectable, signal } from '@angular/core';
import {
  checkbox,
  DynamicFormField,
  fieldGroup,
  formBuilder,
  ISelectionHandler,
  ManualSubmitResult,
  NewSelectionGroup,
  number,
  ScanHandlerResult,
  SelectionConfig,
} from '@kinet-open-network/client/core/forms/ui';
import { CustomersService } from '@kinet-open-network/client/customers/data-access';
import { toast } from '@kinet-open-network/client/shared/ui';
import {
  selectLazyStockItems,
  StockItemsService,
} from '@kinet-open-network/client/stock-items/data-access';
import {
  CoreStockType,
  SelectionItem,
  StockType,
} from '@kinet-open-network/shared/data-access/model';
import {
  IAppStockItem,
  IFlowOrderScannedItem,
  IFlowOrderSelectionItemData,
  stockItemToFlowOrderScannedItem,
} from '@kinet-open-network/shared/data-access/model-pbgas';
import { MenuItem } from 'primeng/api';
import { firstValueFrom } from 'rxjs';

/**
 * App-wide selection handler.
 * Centralises stock-item scan logic (SN lookup, validation, isFull/isSell/isComplaint
 * flag handling), table configuration and manual-input form fields so that
 * SelectionComponent consumers stay generic.
 *
 * Provided at root level in app.config.ts via SELECTION_HANDLER token.
 */
@Injectable()
export class AppSelectionHandler
  implements
    ISelectionHandler<IFlowOrderSelectionItemData, IFlowOrderScannedItem>
{
  private readonly stockItemsService = inject(StockItemsService);
  private readonly customersService = inject(CustomersService);
  readonly isFullScan = signal(true);
  /** Customer id that must hold scanned items; `null` disables the holder check. */
  readonly expectedHolderId = signal<number | null>(null);
  /** Customer id that scanned items must be reserved for; `null` disables the check. */
  readonly expectedReservationPartner = signal<number | null>(null);

  // ── ISelectionHandler.config ───────────────────────────────────────────────

  readonly config = computed<
    SelectionConfig<IFlowOrderSelectionItemData, IFlowOrderScannedItem>
  >(() => ({
    columns: [
      { field: 'name', header: 'Produkt' },
      { field: 'count', header: 'Počet', type: 'number' },
      { field: 'data.isFull', header: 'Plná', type: 'boolean' },
      { field: 'data.isSell', header: 'Predaj', type: 'boolean' },
      { field: 'data.isComplaint', header: 'Reklamácia', type: 'boolean' },
    ],
    tableOptions: {
      expandMode: 'multiple',
      dataKey: (row) => {
        const d = row.data as IFlowOrderSelectionItemData;
        return `${row.id}-${d?.isFull}-${d?.isSell}-${d?.isComplaint}`;
      },
    },
    scanItemLabel: (item) => {
      const si = item as IFlowOrderScannedItem;
      return `${si.sku} - SN: ${si.sn}`;
    },
    inputActions: [
      {
        icon: 'pi pi-filter rotate-180',
        text: true,
        visible: !this.isFullScan(),
        tooltip: 'Prázdna',
        command: () => this.isFullScan.set(true),
      },
      {
        icon: 'pi pi-filter-fill rotate-180',
        text: true,
        visible: this.isFullScan(),
        tooltip: 'Plná',
        command: () => this.isFullScan.set(false),
      },
    ],
  }));

  // ── ISelectionHandler.manualFields ─────────────────────────────────────────

  readonly manualFields: DynamicFormField[] = formBuilder([
    fieldGroup()
      .fieldGroup([
        selectLazyStockItems(this.stockItemsService, {
          label: 'Produkt',
        }).className('col-span-4'),
        number('manualCount', 'Počet')
          .defaultValue(1)
          .props({ min: 1, minDecimals: 0, maxDecimals: 0 }),
        checkbox('isFull', 'Plná').className('pt-9'),
        checkbox('isSell', 'Predaj').className('pt-9'),
        checkbox('isComplaint', 'Reklamácia').className('pt-9'),
      ])
      .fieldGroupClassName('grid grid-cols-8 gap-x-4'),
  ]);

  // ── ISelectionHandler.onScan ───────────────────────────────────────────────

  async onScan(
    code: string,
    items: ReadonlyArray<
      SelectionItem<IFlowOrderSelectionItemData, IFlowOrderScannedItem>
    >,
    requiredAmounts: ReadonlyArray<{ id: string; amount: number }>,
  ): Promise<ScanHandlerResult> {
    const stockItem = await firstValueFrom<IAppStockItem | null>(
      this.stockItemsService.getBySn(code),
    );

    if (!stockItem) {
      return {
        error: `Položka s SN ${code} nebola nájdená.`,
        severity: 'danger',
      };
    }
    if (!stockItem.parentId) {
      return {
        error: `Položka s SN ${code} nemá nadradenú položku.`,
        severity: 'danger',
      };
    }
    if (!stockItem.sn) {
      return {
        error: `Chyba pri skenovaní položky ${stockItem.name ?? stockItem.sku ?? 'Neznáma položka'}.`,
        severity: 'danger',
      };
    }

    const scannedItem = stockItemToFlowOrderScannedItem({
      ...stockItem,
      sn: code,
    });

    const alreadyAdded = items.some((item) =>
      item.scanItems.some((s) => s.sn === scannedItem.sn),
    );
    if (alreadyAdded) {
      return {
        error: `Položka s SN ${code} už bola pridaná.`,
        severity: 'warn',
      };
    }

    const expectedHolder = this.expectedHolderId();

    if (
      !!expectedHolder &&
      !!stockItem.holder &&
      (stockItem.holder !== String(expectedHolder) ||
        stockItem.holderType !== CoreStockType.customer)
    ) {
      void this.warnPartnerMismatch(
        `Fľaša ${code} je od`,
        stockItem.holder,
        stockItem.holderType,
      );
    }

    const expectedReservation = this.expectedReservationPartner();
    if (
      !!expectedReservation &&
      !!stockItem.reservationPartner &&
      stockItem.reservationPartner !== String(expectedReservation)
    ) {
      void this.warnPartnerMismatch(
        `Fľaša ${code} je rezervovaná pre iného zákazníka`,
        stockItem.reservationPartner,
      );
    }

    const existingGroup = [...items]
      .sort(
        (a, b) =>
          (requiredAmounts.find((r) => r.id === b.id)?.amount ?? 0) -
          b.totalCount -
          ((requiredAmounts.find((r) => r.id === a.id)?.amount ?? 0) -
            a.totalCount),
      )
      .find(
        (item) =>
          item.data.isFull === this.isFullScan() &&
          item.data.stockItemId === stockItem.parentId,
      );
    if (existingGroup) {
      return { targetGroupId: existingGroup.id, scannedItem };
    }

    // No existing group — return new group info; consumer decides whether to create or error.
    // id === targetGroupId ensures subsequent scans resolve via the targetGroupId path.
    const newGroup: NewSelectionGroup<IFlowOrderSelectionItemData> = {
      id: crypto.randomUUID(),
      name: stockItem.name ?? stockItem.sku ?? 'Neznáma položka',
      data: {
        isFull: this.isFullScan(),
        isSell: false,
        isComplaint: false,
        stockItemId: stockItem.parentId,
      } satisfies IFlowOrderSelectionItemData,
      scanItems: [scannedItem],
      manualCount: 0,
    };
    return { newGroup, scannedItem };
  }

  private async warnPartnerMismatch(
    message: string,
    customerId?: string,
    holderType?: StockType | null,
  ) {
    let name = customerId ? `${customerId}` : 'neuvedený';
    if (holderType && holderType !== CoreStockType.customer) {
      name = `${holderType} ${name}`;
    } else if (customerId && /^\d+$/.test(customerId)) {
      try {
        const { data } = await this.customersService.queryAsync({
          filters: `id eq ${customerId}`,
          limit: 1,
        });
        name = data[0]?.fullname || name;
      } catch {
        // fall back to the raw owner id
      }
    }
    toast({
      severity: 'warn',
      summary: `${message}: ${name}.`,
      closable: true,
    });
  }

  // ── ISelectionHandler.onManualSubmit ───────────────────────────────────────

  async onManualSubmit(
    model: unknown,
    items: ReadonlyArray<
      SelectionItem<IFlowOrderSelectionItemData, IFlowOrderScannedItem>
    >,
  ): Promise<ManualSubmitResult> {
    const m = model as {
      stockItemId?: string;
      isFull?: boolean;
      isSell?: boolean;
      isComplaint?: boolean;
      manualCount?: number;
    };
    if (!m.stockItemId) return null;

    const existingItem = items.find((i) => {
      const d = i.data;
      return (
        d?.stockItemId === m.stockItemId &&
        d?.isFull == m.isFull &&
        d?.isSell == m.isSell &&
        d?.isComplaint == m.isComplaint
      );
    });
    if (existingItem) {
      return {
        error: `Položka ${existingItem.name} už bola pridaná.`,
        severity: 'danger',
      };
    }

    try {
      const stockItem = await firstValueFrom(
        this.stockItemsService.findOne(m.stockItemId),
      );
      return {
        newItem: new SelectionItem<
          IFlowOrderSelectionItemData,
          IFlowOrderScannedItem
        >({
          id: crypto.randomUUID(),
          name: stockItem.name || stockItem.sku || 'Neznáma položka',
          scanItems: [],
          data: {
            isFull: m.isFull || false,
            isSell: m.isSell || false,
            isComplaint: m.isComplaint || false,
            stockItemId: m.stockItemId,
          },
          manualCount: m.manualCount || 1,
        }),
      };
    } catch {
      return {
        error: `Položka ${m.stockItemId} nebola nájdená.`,
        severity: 'danger',
      };
    }
  }

  // ── ISelectionHandler.moveScanItem ─────────────────────────────────────────

  moveScanItem(
    scanItem: IFlowOrderScannedItem,
    fromItem: SelectionItem<IFlowOrderSelectionItemData, IFlowOrderScannedItem>,
    newData: Partial<IFlowOrderSelectionItemData>,
    items: SelectionItem<IFlowOrderSelectionItemData, IFlowOrderScannedItem>[],
    requiredAmounts: Array<{ id: string; amount: number }>,
  ): SelectionItem<IFlowOrderSelectionItemData, IFlowOrderScannedItem>[] {
    fromItem.scanItems = fromItem.scanItems.filter((i) => i.id !== scanItem.id);

    const targetData: IFlowOrderSelectionItemData = {
      ...fromItem.data,
      ...newData,
    };

    const targetItem = items.find(
      (item) =>
        item.data?.stockItemId === targetData.stockItemId &&
        item.data?.isFull === targetData.isFull &&
        item.data?.isSell === targetData.isSell &&
        item.data?.isComplaint === targetData.isComplaint,
    );

    if (targetItem) {
      targetItem.scanItems.push(scanItem);
    } else {
      items.push(
        new SelectionItem<IFlowOrderSelectionItemData, IFlowOrderScannedItem>({
          id: crypto.randomUUID(),
          name: fromItem.name,
          scanItems: [scanItem],
          data: targetData,
          manualCount: 0,
        }),
      );
    }

    const requiredAmt = fromItem.getRequiredAmount(requiredAmounts);
    const updatedItems =
      fromItem.scanItems.length === 0 &&
      fromItem.manualCount === 0 &&
      requiredAmt === 0
        ? items.filter((i) => i.id !== fromItem.id)
        : items;

    return [...updatedItems];
  }

  // ── ISelectionHandler.getScanItemActions ────────────────────────────────────

  getScanItemActions(
    item: SelectionItem<IFlowOrderSelectionItemData, IFlowOrderScannedItem>,
    scanItem: IFlowOrderScannedItem,
    items: ReadonlyArray<
      SelectionItem<IFlowOrderSelectionItemData, IFlowOrderScannedItem>
    >,
    requiredAmounts: ReadonlyArray<{ id: string; amount: number }>,
    onUpdate: (
      newItems: SelectionItem<
        IFlowOrderSelectionItemData,
        IFlowOrderScannedItem
      >[],
    ) => void,
  ): MenuItem[] {
    const move = (newData: Partial<IFlowOrderSelectionItemData>) => {
      const result = this.moveScanItem(
        scanItem,
        item,
        newData,
        [...items],
        [...requiredAmounts],
      );
      onUpdate(result);
    };
    return [
      {
        icon: item.data?.isFull ? 'pi pi-check-circle' : 'pi pi-circle',
        label: 'Plná',
        command: () => move({ isFull: !item.data?.isFull }),
      },
      {
        icon: item.data?.isSell ? 'pi pi-check-circle' : 'pi pi-circle',
        label: 'Predaj',
        command: () => move({ isSell: !item.data?.isSell }),
      },
      {
        icon: item.data?.isComplaint ? 'pi pi-check-circle' : 'pi pi-circle',
        label: 'Reklamácia',
        command: () => move({ isComplaint: !item.data?.isComplaint }),
      },
    ];
  }
}
